import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function InstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);
    const [installed, setInstalled] = useState(false);
    const [inIframe, setInIframe] = useState(false);
    const [waiting, setWaiting] = useState(true);
    const [scrolledAway, setScrolledAway] = useState(false);
    const isAndroid = /Android/i.test(navigator.userAgent);

    // Owner report 2026-10-24: the fixed banner covered page text while
    // scrolling on phones — it now slides away once you scroll and returns
    // when you're back near the top
    useEffect(() => {
        const onScroll = () => setScrolledAway(window.scrollY > 240);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const standalone =
            window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
        if (standalone || window.__pcfInstalled || sessionStorage.getItem("pcf_install_dismissed")) return;
        setInIframe(window.self !== window.top);
        // Pick up the install event even if Chrome fired it before React mounted
        if (window.__pcfInstallEvent) {
            setDeferred(window.__pcfInstallEvent);
            setWaiting(false);
        }
        const onPrompt = (e) => {
            e.preventDefault();
            window.__pcfInstallEvent = e;
            setDeferred(e);
            setWaiting(false);
        };
        const onReady = () => {
            if (window.__pcfInstallEvent) {
                setDeferred(window.__pcfInstallEvent);
                setWaiting(false);
            }
        };
        const onInstalled = () => {
            setInstalled(true);
            setVisible(false);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("pcf-install-ready", onReady);
        window.addEventListener("appinstalled", onInstalled);
        window.addEventListener("pcf-installed", onInstalled);
        const t = setTimeout(() => setVisible(true), 1200);
        // Give Chrome up to 8s to arm one-tap install before offering manual fallback
        const t2 = setTimeout(() => setWaiting(false), 8000);
        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("pcf-install-ready", onReady);
            window.removeEventListener("appinstalled", onInstalled);
            window.removeEventListener("pcf-installed", onInstalled);
            clearTimeout(t);
            clearTimeout(t2);
        };
    }, []);

    const install = async () => {
        if (deferred) {
            deferred.prompt();
            const { outcome } = await deferred.userChoice;
            if (outcome === "accepted") {
                setVisible(false);
                toast.success("INSTALLING — SEE YOU ON THE HOME SCREEN, DRIVER");
            }
            window.__pcfInstallEvent = null;
            setDeferred(null);
            return;
        }
        if (waiting) return; // still arming — button is disabled anyway
        if (inIframe) {
            window.open(window.location.href, "_blank", "noopener");
            return;
        }
        if (isAndroid) {
            // Embedded viewers (in-app browsers) never offer installs — bail out to real Chrome
            toast("OPENING IN CHROME", {
                duration: 8000,
                description:
                    "Tap INSTALL THE APP on the banner there — one tap, straight to your home screen. If Chrome didn't open, use this viewer's menu (⋮) → Open in Chrome first.",
            });
            const { host, pathname } = window.location;
            setTimeout(() => {
                window.location.href = `intent://${host}${pathname}#Intent;scheme=https;package=com.android.chrome;end`;
            }, 400);
            return;
        }
        toast("INSTALL MANUALLY", {
            description:
                "Android: browser menu → Install app / Add to Home screen. iPhone: Share → Add to Home Screen.",
        });
    };

    if (!visible || installed) return null;

    return (
        <div
            className={`fixed inset-x-0 top-16 z-50 flex justify-center px-3 transition-all duration-300 sm:top-20 sm:px-4 ${
                scrolledAway ? "pointer-events-none -translate-y-[150%] opacity-0" : ""
            }`}
        >
            <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                data-testid="install-prompt"
                className="max-h-[70vh] w-full max-w-md overflow-y-auto border-2 border-[#0E0D0C] bg-[#E5A93C] p-3 shadow-[6px_6px_0px_#0E0D0C] sm:p-4 landscape:max-w-lg"
            >
                <div className="flex items-center gap-3 sm:gap-4">
                    <img
                        src="/icon-192.png"
                        alt="App icon"
                        className="h-12 w-12 shrink-0 border-2 border-[#0E0D0C] sm:h-14 sm:w-14"
                    />
                    <div className="min-w-0 flex-1">
                        <p className="font-display text-lg leading-none tracking-widest text-[#161413] sm:text-xl">
                            INSTALL NOW
                        </p>
                        <p className="font-mono2 mt-1 text-[9px] leading-snug tracking-widest text-[#161413]/80 sm:text-[10px]">
                            PUT CHA FOOT IN IT DRIVER! — FULLSCREEN APP, WORKS OFFLINE-SHELL, ANY ROTATION
                        </p>
                    </div>
                </div>
                <div className="mt-3 flex gap-2">
                    <button
                        data-testid="install-now-btn"
                        onClick={install}
                        disabled={!deferred && waiting && !inIframe}
                        className={`btn-press font-display flex-1 border-2 border-[#0E0D0C] px-3 py-2.5 text-sm tracking-widest text-[#F6F2E8] shadow-[3px_3px_0px_#0E0D0C] sm:px-4 sm:text-base ${
                            !deferred && waiting && !inIframe ? "cursor-wait bg-[#8C8377]" : "bg-[#D32F2F]"
                        }`}
                    >
                        {deferred
                            ? "INSTALL THE APP"
                            : waiting && !inIframe
                              ? "PREPARING ONE-TAP INSTALL…"
                              : inIframe
                                ? "OPEN IN BROWSER & INSTALL"
                                : isAndroid
                                  ? "INSTALL VIA CHROME"
                                  : "HOW TO INSTALL"}
                    </button>
                    <button
                        data-testid="install-dismiss-btn"
                        onClick={() => {
                            setVisible(false);
                            sessionStorage.setItem("pcf_install_dismissed", "1");
                        }}
                        className="btn-press font-display border-2 border-[#0E0D0C] bg-[#161413] px-3 py-2.5 text-sm tracking-widest text-[#8C8377] shadow-[3px_3px_0px_#0E0D0C] sm:px-4 sm:text-base"
                    >
                        LATER
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
